import dynamic from "next/dynamic";

import { useCallback, useMemo, useState } from "react";

import { debounce } from "lodash";
import { twMerge } from "tailwind-merge";

import { faSpinner } from "@fortawesome/free-solid-svg-icons/faSpinner";

import { LoaderSingleLine } from "components/customElements/loader";
import FontAwesomeIcon from "components/FontAwesomeIcon";
import { SK_CHANNEL } from "hooks/useActionCable";
import useAuthentication from "hooks/useAuthentication";
import { useChannel } from "hooks/useChannel";
import useMediaQuery from "hooks/useMediaQuery";
import { fetchApi } from "utils/baseFetcher";

import Interests from "./Interests";
import MuteSettings from "./MuteSettings";
import ToggleButton from "./ToggleButton";
import useFeedSettings from "./useFeedSettings";

const Modal = dynamic(() => import("react-modal"), { ssr: false });

type FeedSettingsModalProps = {
  onClose: () => void;
};

const FeedSettingsModal = ({ onClose }: FeedSettingsModalProps) => {
  const { account: loggedInAccount, accountId } = useAuthentication();

  const requestFeedCount = useMemo(
    () =>
      debounce(() => {
        fetchApi("/api/v2/accounts/request-feed-count", {
          method: "POST",
        });
      }, 1000),
    []
  );
  const onChange = useCallback(() => {
    setTotalInsightsLoading(true);
    requestFeedCount();
  }, [requestFeedCount]);
  const feedSettings = useFeedSettings({ autoSave: true, onChange });
  const {
    activeTab,
    onChangeActiveTab,
    geographies,
    countries,
    sectors,
    contentVerticals,
    isSaveDisabled,
  } = feedSettings;

  const contentLoaded =
    geographies?.length > 0 &&
    countries?.length > 0 &&
    sectors?.length > 0 &&
    contentVerticals?.length > 0;

  const { mobile } = useMediaQuery();

  const [totalInsights, setTotalInsights] = useState(0);
  const [totalInsightsLoading, setTotalInsightsLoading] = useState(false);
  useChannel({
    key: "FeedSettingModal",
    name: `private-account-${accountId}`,
    channel: {
      channel: SK_CHANNEL,
      room: `private-account-${accountId}`,
    },
    onReceived: (message: any) => {
      const { event, total_insights } = message;
      if (event === "account-feed-count") {
        setTotalInsights(total_insights);
        setTotalInsightsLoading(false);
      }
    },
  });

  return (
    <Modal
      isOpen={true}
      portalClassName="z-[1999]"
      overlayClassName={twMerge(
        mobile ? "p-[15px] pt-[50px] overflow-x-hidden" : "",
        "z-[1999] fixed inset-0 bg-[rgba(31,31,32,.77)] overflow-y-auto"
      )}
      className={twMerge(
        mobile
          ? "top-[50px] px-[25px] py-[30px]"
          : "relative top-[-80px] min-h-[60vh] w-[1000px] mx-auto my-[130px] px-[25px] py-[30px]",
        "outline-none rounded-xl bg-white",
        "flex flex-col"
      )}
      onAfterOpen={() => {
        document.body.style.overflowY = "hidden";
      }}
      onAfterClose={() => {
        document.body.style.overflowY = "";
      }}
    >
      {!loggedInAccount?.isMandatesEmpty && (
        <button
          onClick={onClose}
          className="absolute top-[0px] right-[15px] border-0 bg-transparent text-[40px] text-[#999] hover:text-active-black"
        >
          ×
        </button>
      )}
      <h3 className={twMerge("mb-[30px]", "text-[22px] font-bold")}>
        Feed Settings
      </h3>
      <div className="flex flex-col">
        <div className="mb-8">
          <ToggleButton
            className="mr-5"
            onClick={onChangeActiveTab}
            name="interests"
            activeTab={activeTab}
          >
            Interests
          </ToggleButton>
          <ToggleButton
            name="muteOptions"
            onClick={onChangeActiveTab}
            activeTab={activeTab}
          >
            Mute Options
          </ToggleButton>
        </div>
        <div
          className={twMerge(
            "space-y-[38px] min-h-[30vh]",
            !contentLoaded && "flex justify-center items-center"
          )}
        >
          {activeTab === "interests" ? (
            contentLoaded ? (
              <>
                {loggedInAccount?.isPreviewPass && (
                  <div className="flex justify-center items-center mb-[15px] min-h-[40px]">
                    {!isSaveDisabled &&
                    !!totalInsights &&
                    !totalInsightsLoading ? (
                      <div className="text-center px-[15px] py-[5px] bg-[#DBFEF0] rounded w-[450px] mx-auto j">
                        <b>{totalInsights}</b> insights published in last 30
                        days matching your settings
                      </div>
                    ) : !isSaveDisabled && totalInsightsLoading ? (
                      <div className="w-[450px] mx-auto">
                        <LoaderSingleLine />
                      </div>
                    ) : !isSaveDisabled &&
                      !totalInsights &&
                      !totalInsightsLoading ? (
                      <div className="text-center px-[15px] py-[5px] bg-[#FFEBE6] roundedw-[450px] mx-auto">
                        There are no insights published in last 30 days matching
                        your settings
                      </div>
                    ) : (
                      <></>
                    )}
                  </div>
                )}
                <Interests {...feedSettings} />
              </>
            ) : (
              <FontAwesomeIcon
                icon={faSpinner}
                spin
                className="text-[50px] text-[#ccc]"
              />
            )
          ) : null}
          {activeTab === "muteOptions" && <MuteSettings />}
        </div>
      </div>
      <div className="flex items-stretch w-full mt-[30px] md:mt-auto md:pt-[30px]">
        <div className="flex-1 text-right">
          <button
            className={twMerge(
              "min-w-[6em] px-[15px] py-[8px]",
              "rounded-full border-0 bg-smart-teal hover:bg-smart-teal-500 text-white"
            )}
            onClick={onClose}
          >
            OK
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default FeedSettingsModal;
